/**
 * CheckoutPanel — the reusable inner checkout content (plan + card + CTA),
 * ported from the prototype's ``CheckoutPanel``.
 *
 *   mode "trial"       → starts the free trial (card up front, no charge today)
 *   mode "resubscribe" → charges today, goes active (from locked)
 *   mode "addcard"     → attaches a card mid-trial (no charge), optional plan switch
 *
 * Card capture is the real Stripe flow: we create a **SetupIntent**
 * (``POST /billing/stripe/setup_intent`` → ``{ client_secret }``), mount
 * ``<Elements>`` with it, and confirm via ``StripePaymentElement`` (no charge).
 * All billing decisions live in ``useBilling`` — this component only chooses
 * which action to call for the active mode.
 *
 * Trial mode takes the card up front and has no skip of its own: its one trial-mode caller,
 * UnlockModal, draws the way out ("Keep playing free", design-trial-sheet.md, 2026-10-27).
 */

import { useEffect, useState } from "react";
import { captureException } from "@sentry/browser";
import { Elements } from "@stripe/react-stripe-js";
// The `/pure` entry, NOT the package root: importing the root injects
// js.stripe.com/v3 as a side effect, and this module is on the boot path (via
// BillingChrome), so every page — the landing a child sees first included —
// loaded Stripe.js and its device-signals iframe (m.stripe.network). With
// `/pure` the script loads on the first loadStripe() call, i.e. when a grown-up
// opens checkout. ipad-native-shell Decision 9 (14.9) and Decision 12.
import { loadStripe } from "@stripe/stripe-js/pure";
import type { Stripe } from "@stripe/stripe-js";
import { ChromeTokens } from "../chrome/tokens";
import { Icon } from "../chrome/Icon";
import { PlanPicker } from "./PlanPicker";
import { StripePaymentElement } from "./StripePaymentElement";
import { StripeSecured } from "./StripeSecured";
import { createPortal } from "react-dom";
import { CardSubmitButton } from "./CardSubmitButton";
import { noteMailingPrompt, requestMailingPrompt } from "./mailingAddress";
import { useBilling } from "./useBilling";
import { createSetupIntent } from "./api";
import { DEFAULT_TRIAL_DAYS, STRIPE_PUBLISHABLE_KEY, money, planById } from "./plans";
import { ensureBillingKeyframes } from "./keyframes";
import type { CardCaptureResult, PlanId } from "./types";

export const CARD_UNAVAILABLE = "Card sign-up isn't available right now. You can keep playing free.";
export const CARD_FAILED = "That didn't go through, and nothing changed. Please try again in a moment.";

export type CheckoutMode = "trial" | "resubscribe" | "addcard";

export interface CheckoutPanelProps {
  planId?: PlanId;
  mode?: CheckoutMode;
  showPlanPicker?: boolean;
  ctaLabel?: string;
  trialDays?: number;
  onPlan?: (id: PlanId) => void;
  onDone?: (id: PlanId) => void;
  /** The sheet's footer: the submit button is pinned there, the form scrolls above it. */
  footer?: HTMLElement | null;
}

// Memoize the Stripe.js loader per publishable key (loadStripe must run once).
let _stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> | null {
  if (!STRIPE_PUBLISHABLE_KEY) return null;
  if (!_stripePromise) _stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return _stripePromise;
}

export function CheckoutPanel({
  planId,
  mode = "trial",
  showPlanPicker = true,
  ctaLabel,
  trialDays = DEFAULT_TRIAL_DAYS,
  onPlan,
  onDone,
  footer = null,
}: CheckoutPanelProps): JSX.Element {
  const b = useBilling();
  const [pid, setPid] = useState<PlanId>(planId ?? "family");
  const [phase, setPhase] = useState<"form" | "processing" | "done">("form");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // A SetupIntent confirms once: after a failed submit the card form needs a new one.
  const [setupAttempt, setSetupAttempt] = useState(0);
  const plan = planById(pid);
  const stripePromise = getStripe();

  ensureBillingKeyframes();

  // Fetch a SetupIntent client secret once (no charge — just saves the card).
  useEffect(() => {
    if (!stripePromise) {
      // A build without the key has no card form at all (2026-10-31: every build had none).
      captureException(new Error("checkout: this build has no Stripe publishable key"));
      return;
    }
    let cancelled = false;
    setSetupError(null);
    createSetupIntent()
      .then((res) => {
        if (!cancelled) setClientSecret(res.client_secret);
      })
      .catch(() => {
        if (!cancelled) setSetupError("We couldn't open the secure card form. Please try again in a moment.");
      });
    return () => {
      cancelled = true;
    };
  }, [stripePromise, setupAttempt]);

  const setPlan = (id: PlanId) => {
    setPid(id);
    onPlan?.(id);
  };

  const handleCardComplete = async (card: CardCaptureResult) => {
    setPhase("processing");
    setSubmitError(null);
    try {
      if (mode === "resubscribe") {
        await b.subscribeNow({ plan_id: pid, ...card });
      } else if (mode === "addcard") {
        await b.attachCard(card);
        if (pid !== b.view?.planId) await b.changePlan(pid);
      } else {
        await b.startTrial({ plan_id: pid, ...card });
      }
      setPhase("done");
      // Ask once for a mailing address (train 17). Only adding a card stays on this page; a
      // trial or a subscribe leaves for /subscribed, and that page asks.
      if (mode === "addcard") requestMailingPrompt();
      else noteMailingPrompt();
      window.setTimeout(() => onDone?.(pid), 1100);
    } catch {
      setPhase("form");
      setSubmitError(CARD_FAILED);
      setClientSecret(null);
      setSetupAttempt((n) => n + 1);
    }
  };

  if (phase === "done") {
    return (
      <div style={{ textAlign: "center", padding: `${ChromeTokens.spacing.xl}px 0`, fontFamily: ChromeTokens.font.ui, color: ChromeTokens.color.ink }}>
        <div
          data-wbm-pop
          style={{
            width: 64,
            height: 64,
            borderRadius: ChromeTokens.radius.pill,
            margin: `0 auto ${ChromeTokens.spacing.md + 2}px`,
            background: `color-mix(in srgb, var(--chrome-color-grass) 26%, var(--chrome-color-base))`,
            display: "grid",
            placeItems: "center",
            animation: `wbm-billing-pop 360ms ${ChromeTokens.motion.softEase}`,
          }}
        >
          <Icon name="check" size={30} stroke={ChromeTokens.color.clay} />
        </div>
        <h3 style={{ margin: "0 0 4px", fontSize: 20 }}>
          {mode === "resubscribe" ? "You're back in!" : mode === "addcard" ? "Card saved!" : "Your free trial is on!"}
        </h3>
        <p style={{ color: ChromeTokens.color.inkMuted, fontSize: 14, margin: 0 }}>
          {mode === "resubscribe"
            ? `${plan.name} plan · ${money(plan.price)}/mo`
            : mode === "addcard"
              ? `You're all set — ${plan.name} starts when your trial ends. Cancel anytime.`
              : `${trialDays} free days, then ${money(plan.price)}/mo. Cancel anytime.`}
        </p>
      </div>
    );
  }

  const defaultCta =
    mode === "resubscribe"
      ? `Subscribe · ${money(plan.price)}/mo`
      : mode === "addcard"
        ? "Save card"
        : `Start my ${trialDays}-day free trial`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.lg, fontFamily: ChromeTokens.font.ui, color: ChromeTokens.color.ink }}>
      {showPlanPicker && <PlanPicker value={pid} onChange={setPlan} />}

      {/* trial / charge summary */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: ChromeTokens.spacing.sm + 2,
          padding: `11px 13px`,
          borderRadius: ChromeTokens.radius.md,
          background: `color-mix(in srgb, var(--chrome-color-sun) 18%, var(--chrome-color-base))`,
        }}
      >
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: ChromeTokens.radius.pill,
            background: ChromeTokens.color.base,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Icon name={mode === "resubscribe" ? "sparkles" : "gift"} size={17} stroke={ChromeTokens.color.clay} />
        </span>
        <div style={{ fontSize: 13, lineHeight: 1.4 }}>
          {mode === "resubscribe" ? (
            <>
              <strong>{plan.name} plan</strong> — {money(plan.price)}/mo, starting today. Cancel anytime.
            </>
          ) : mode === "addcard" ? (
            <>
              <strong>No charge today.</strong> {plan.name} — {money(plan.price)}/mo — begins only when your free trial ends. Cancel anytime.
            </>
          ) : (
            <>
              <strong>Free for {trialDays} days.</strong> Then {money(plan.price)}/mo. Cancel anytime.
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.sm }}>
        <label style={{ fontSize: 13, fontWeight: 800, color: ChromeTokens.color.ink }}>Card details</label>
        {submitError && (
          <p role="alert" style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: ChromeTokens.color.danger, lineHeight: 1.4 }}>
            {submitError}
          </p>
        )}
        {stripePromise && clientSecret ? (
          <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret, appearance: { theme: "flat" } }}>
            <StripePaymentElement
              submitLabel={ctaLabel ?? defaultCta}
              busy={phase === "processing"}
              onComplete={handleCardComplete}
              footer={footer}
            />
          </Elements>
        ) : (
          <div
            style={{
              borderRadius: ChromeTokens.radius.md,
              border: `1.5px solid ${ChromeTokens.color.baseMuted}`,
              padding: ChromeTokens.spacing.lg,
              background: ChromeTokens.color.base,
              fontSize: 13,
              color: ChromeTokens.color.inkMuted,
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            {!stripePromise ? CARD_UNAVAILABLE : setupError ?? "The secure card form is getting ready…"}
            {stripePromise && (
              <div style={{ marginTop: ChromeTokens.spacing.md }}>
                <StripeSecured />
              </div>
            )}
            {stripePromise && footer && createPortal(
              <CardSubmitButton label={ctaLabel ?? defaultCta} waiting={false} disabled />,
              footer,
            )}
          </div>
        )}
      </div>


      <p style={{ fontSize: 11, color: ChromeTokens.color.inkMuted, textAlign: "center", lineHeight: 1.5, margin: 0 }}>
        {mode === "resubscribe"
          ? "You'll be charged today and monthly after that."
          : `You won't be charged until your ${trialDays}-day trial ends.`}{" "}
        By continuing you agree to our Terms. A grown-up should set this up.
      </p>
    </div>
  );
}
